import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, Grid, TextField, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import DataDisplay from '../../components/DataDisplay';
import { API } from '../../api/api_setup';
import { useAuth } from '../../hooks/useAuth';


const TopicThreadDetailsPanel = ({ clusterName }) => {
  const { user } = useAuth()
  const [isLoading, setIsLoading] = useState(true)
  const [rows, setRows] = useState([])
  const [grouped, setGrouped] = useState({})
  const [dsrcType, setDsrcType] = useState('All')
  const [selectedDate, setSelectedDate] = useState(null)

  const columns = [
    { field: 'timestamp', headerName: 'Timestamp', width: 220 },
    { field: 'datasource_id', headerName: 'Datasource ID', width: 270 },
    { field: 'datasource_type', headerName: 'Datasource Type', width: 160 },
    { field: 'running_consumers', headerName: 'Running Consumers', type: 'number', width: 170 },
    { field: 'topic', headerName: 'Topic', width: 320 },
    { field: 'threads', headerName: 'Threads', type: 'number', width: 120 },
    { field: 'pending', headerName: 'Pending', type: 'number', width: 140 },
  ]

  useEffect(() => {
    setIsLoading(true)
    const headers = { Authorization: `Bearer ${user.token}` }
    API.get(`/cluster_debug/topic_thread_details/${clusterName}?days=7`, { headers })
      .then((allRes) => {
        const data = allRes.data.data || []
        setRows(data)
        const groupedData = data.reduce((acc, row) => {
          const key = row.datasource_type || 'Unknown'
          acc[key] = acc[key] || []
          acc[key].push(row)
          return acc
        }, {})
        setGrouped(groupedData)
        setDsrcType((prev) => {
          if (prev !== 'All' && groupedData[prev]) {
            return prev
          }
          const keys = Object.keys(groupedData)
          if (prev === 'All' && keys.length > 0) {
            return keys[0]
          }
          if (prev !== 'All' && !groupedData[prev]) {
            return keys[0] || 'All'
          }
          return prev
        })
      })
      .catch(() => { })
      .finally(() => setIsLoading(false))
  }, [clusterName, user.token])

  const filteredRows = useMemo(() => {
    let filtered = rows

    // Apply date filter
    if (selectedDate) {
      const selectedDateStr = dayjs(selectedDate).format('YYYY-MM-DD')
      filtered = filtered.filter((row) => {
        if (!row.timestamp) return false
        const rowDateStr = String(row.timestamp).slice(0, 10)
        return rowDateStr === selectedDateStr
      })
    }

    // Apply datasource type filter
    if (dsrcType !== 'All') {
      filtered = filtered.filter((row) => row.datasource_type === dsrcType)
    }

    return filtered
  }, [rows, selectedDate, dsrcType])

  return (
    <>
      <Grid container alignItems={'center'}>
        <Grid item sx={{ paddingLeft: '10px', paddingRight: '20px' }}>
          <Typography variant='h6' color={'#313638'}>{'Topic and Thread Details'}</Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} sx={{ padding: '10px' }}>
          <Autocomplete
            options={["All", ...Object.keys(grouped)]}
            value={dsrcType}
            onChange={(e, newValue) => setDsrcType(newValue || 'All')}
            renderInput={(params) => <TextField {...params} label="Datasource Type" size='small' />}
            filterSelectedOptions
            disableClearable
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={3} sx={{ padding: '10px' }}>
          <DatePicker
            label="Filter by Date"
            value={selectedDate}
            onChange={(newValue) => setSelectedDate(newValue)}
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true
              }
            }}
          />
        </Grid>
      </Grid>
      <Grid item lg={12} md={12} sx={{ paddingBottom: '16px' }}>
        <DataDisplay
          rows={filteredRows.map((r, idx) => ({ id: idx + 1, ...r }))}
          columns={columns}
          loading={isLoading}
          sx={{ margin: '10px', marginTop: '0px' }}
          density='compact'
        />
      </Grid>
    </>
  );
}

export default TopicThreadDetailsPanel;
